/**
 * Bidirectional lesson video call over WebRTC, signaled through the existing
 * live-lesson WebSocket ('rtc-offer'/'rtc-answer'/'rtc-ice' relays).
 *
 * Uses the perfect-negotiation pattern (one polite peer — the student — and
 * one impolite — the coach), so both sides can (re)create their connection
 * whenever presence flips and glare resolves itself. Mute / camera-off are
 * implemented by disabling the local tracks (no renegotiation needed) and
 * mirrored to the other side via 'media-state' so the UI can show badges.
 * Everyone ENTERS the room muted and with the camera off — audio/video only
 * flows after a deliberate unmute / camera-on.
 *
 * Signals can arrive before the local peer connection exists (the other side
 * reacts to presence instantly, while our getUserMedia is still opening), so
 * they are queued and replayed in order once the connection is up — dropping
 * the first offer would deadlock the call (the impolite side never accepts a
 * counter-offer while its own is outstanding).
 *
 * Note: the call captures its own mic stream WITH echo cancellation — the
 * pitch engine keeps its separate raw (unprocessed) capture in MicSession.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface RemoteMediaState {
  muted: boolean;
  videoOff: boolean;
}

export type RtcSignal =
  | { type: 'rtc-offer'; sdp: RTCSessionDescriptionInit }
  | { type: 'rtc-answer'; sdp: RTCSessionDescriptionInit }
  | { type: 'rtc-ice'; candidate: RTCIceCandidateInit | null }
  | { type: 'media-state'; muted: boolean; videoOff: boolean };

const ICE_SERVERS = [{ urls: 'stun:stun.l.google.com:19302' }];

export function useVideoCall(opts: {
  /** Local capture runs while true (user has joined the room). */
  enabled: boolean;
  /** The peer connection lives while the other party is present too. */
  otherPresent: boolean;
  /** Perfect-negotiation role — exactly one side is polite (the student). */
  polite: boolean;
  send: (msg: RtcSignal) => void;
}) {
  const { enabled, otherPresent, polite, send } = opts;
  const sendRef = useRef(send);
  sendRef.current = send;

  const localStreamRef = useRef<MediaStream | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [mediaError, setMediaError] = useState<unknown>(null);
  // deliberate opt-in: both sides start muted with the camera off
  const [muted, setMutedState] = useState(true);
  const [videoOff, setVideoOffState] = useState(true);
  const mutedRef = useRef(true);
  const videoOffRef = useRef(true);
  const [remoteState, setRemoteState] = useState<RemoteMediaState | null>(null);
  const [connectionState, setConnectionState] = useState<RTCPeerConnectionState>('new');

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const makingOfferRef = useRef(false);
  const ignoreOfferRef = useRef(false);
  /** Signals received before the pc existed (or while draining), in order. */
  const pendingRef = useRef<Exclude<RtcSignal, { type: 'media-state' }>[]>([]);
  const flushingRef = useRef(false);

  // ── local capture (independent of the peer, so the self-view works early) ─
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      setMediaError(null);
      let stream: MediaStream | null = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true },
          video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
        });
      } catch {
        try {
          // no camera (or denied) — audio-only call still works
          stream = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true },
          });
        } catch (err) {
          if (!cancelled) setMediaError(err);
          return;
        }
      }
      if (cancelled) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      // apply current toggle state to the fresh tracks
      stream.getAudioTracks().forEach((t) => (t.enabled = !mutedRef.current));
      stream.getVideoTracks().forEach((t) => (t.enabled = !videoOffRef.current));
      localStreamRef.current = stream;
      setLocalStream(stream);
    })();
    return () => {
      cancelled = true;
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
      setLocalStream(null);
    };
  }, [enabled]);

  // ── signaling (page routes rtc-* / media-state messages here) ─────────────
  const applySignal = useCallback(
    async (pc: RTCPeerConnection, msg: Exclude<RtcSignal, { type: 'media-state' }>) => {
      try {
        if (msg.type === 'rtc-offer' || msg.type === 'rtc-answer') {
          const offerCollision =
            msg.type === 'rtc-offer' && (makingOfferRef.current || pc.signalingState !== 'stable');
          ignoreOfferRef.current = !polite && offerCollision;
          if (ignoreOfferRef.current) return;
          await pc.setRemoteDescription(msg.sdp);
          if (msg.type === 'rtc-offer') {
            await pc.setLocalDescription();
            sendRef.current({ type: 'rtc-answer', sdp: pc.localDescription!.toJSON() });
          }
        } else if (msg.candidate) {
          try {
            await pc.addIceCandidate(msg.candidate);
          } catch (err) {
            if (!ignoreOfferRef.current) throw err;
          }
        }
      } catch {
        /* glare/timing hiccup — perfect negotiation converges on the next offer */
      }
    },
    [polite],
  );

  // ── peer connection while both parties are in the room ────────────────────
  useEffect(() => {
    if (!enabled || !otherPresent || !localStream) return;
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    pcRef.current = pc;
    for (const track of localStream.getTracks()) pc.addTrack(track, localStream);

    pc.onnegotiationneeded = async () => {
      try {
        makingOfferRef.current = true;
        await pc.setLocalDescription();
        sendRef.current({ type: 'rtc-offer', sdp: pc.localDescription!.toJSON() });
      } catch {
        /* a later negotiation recovers */
      } finally {
        makingOfferRef.current = false;
      }
    };
    pc.onicecandidate = (e) =>
      sendRef.current({ type: 'rtc-ice', candidate: e.candidate ? e.candidate.toJSON() : null });
    pc.ontrack = (e) => setRemoteStream(e.streams[0] ?? null);
    pc.onconnectionstatechange = () => setConnectionState(pc.connectionState);

    // tell the (possibly just-joined) other side where our toggles stand
    sendRef.current({ type: 'media-state', muted: mutedRef.current, videoOff: videoOffRef.current });

    // drain signals that arrived while media was still opening (typically the
    // other side's initial offer + its ICE candidates) — in order, and keep
    // queueing anything that lands mid-drain
    if (pendingRef.current.length > 0) {
      flushingRef.current = true;
      void (async () => {
        while (pendingRef.current.length > 0 && pcRef.current === pc) {
          await applySignal(pc, pendingRef.current.shift()!);
        }
        flushingRef.current = false;
      })();
    }

    return () => {
      pc.close();
      pcRef.current = null;
      pendingRef.current = []; // stale SDP from a torn-down session is useless
      flushingRef.current = false;
      setRemoteStream(null);
      setRemoteState(null);
      setConnectionState('new');
    };
  }, [enabled, otherPresent, localStream, applySignal]);

  // ── incoming signals (pages route rtc-* / media-state messages here) ──────
  const handleSignal = useCallback(
    async (msg: RtcSignal) => {
      if (msg.type === 'media-state') {
        setRemoteState({ muted: msg.muted, videoOff: msg.videoOff });
        return;
      }
      const pc = pcRef.current;
      // queue while the pc doesn't exist yet or the backlog is still draining,
      // so SDP/ICE ordering is preserved end to end
      if (!pc || flushingRef.current) {
        if (pendingRef.current.length < 64) pendingRef.current.push(msg);
        return;
      }
      await applySignal(pc, msg);
    },
    [applySignal],
  );

  const announceState = useCallback(() => {
    sendRef.current({ type: 'media-state', muted: mutedRef.current, videoOff: videoOffRef.current });
  }, []);

  const toggleMute = useCallback(() => {
    mutedRef.current = !mutedRef.current;
    setMutedState(mutedRef.current);
    localStreamRef.current?.getAudioTracks().forEach((t) => (t.enabled = !mutedRef.current));
    announceState();
  }, [announceState]);

  const toggleVideo = useCallback(() => {
    videoOffRef.current = !videoOffRef.current;
    setVideoOffState(videoOffRef.current);
    localStreamRef.current?.getVideoTracks().forEach((t) => (t.enabled = !videoOffRef.current));
    announceState();
  }, [announceState]);

  return {
    localStream,
    remoteStream,
    hasCamera: (localStream?.getVideoTracks().length ?? 0) > 0,
    muted,
    videoOff,
    toggleMute,
    toggleVideo,
    remoteState,
    connectionState,
    mediaError,
    handleSignal,
    announceState,
  };
}

export type VideoCall = ReturnType<typeof useVideoCall>;

export function isRtcSignal(msg: { type?: string }): msg is RtcSignal {
  return (
    msg.type === 'rtc-offer' ||
    msg.type === 'rtc-answer' ||
    msg.type === 'rtc-ice' ||
    msg.type === 'media-state'
  );
}
